/**
 * useTemplateWorkspace Hook
 *
 * Headless hook for managing multiple templates in the template viewer.
 * Persists workspace state to localStorage for session continuity.
 * Extracted from TemplateWorkspaceManager for use in toolbar-based template selection.
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { TemplateWorkspaceSchema } from "@/schemas/templateWorkspace";
import { getAllTemplates, getTemplateById, TemplateData } from "@/utils/templateLoader";
import {
  Page,
  Component,
  Viewport,
  ProjectVisibility,
  ProjectStatus,
  PageTemplate,
  ProjectRole,
  NavigationStyle,
  ComponentType,
  type CanvasSettings,
  type Position,
  type ResponsivePosition,
  type Size,
} from "@/types";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { isContainerType } from "@/utils/componentTypes";

const WORKSPACE_STORAGE_KEY = "terramatrix-template-workspace";

export const TEMPLATE_VIEWER_DEFAULT_CANVAS_SETTINGS: CanvasSettings = {
  background: { type: "color", color: "hsl(var(--background))" },
  grid: {
    enabled: false,
    size: 20,
    color: "#e5e7eb",
    opacity: 0.5,
    style: "solid",
    majorLines: {
      enabled: false,
      interval: 5,
      color: "#d1d5db",
      opacity: 0.5,
    },
    snapToGrid: false,
    snapThreshold: 5,
  },
  layout: {
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
    maxWidth: null,
    maxHeight: null,
    centerContent: false,
    overflow: "visible",
  },
  theme: {
    name: "default",
    colorScheme: "auto",
    accentColor: "#3b82f6",
    borderRadius: 4,
    shadows: true,
    animations: true,
  },
  responsive: {
    breakpoints: { desktop: 1280, tablet: 768, mobile: 375 },
    scaling: { desktop: 1, tablet: 1, mobile: 1 },
    hideElementsOnMobile: false,
    touchOptimization: true,
  },
};

const DEFAULT_POSITION: Position = { x: 0, y: 0, z: 0 };
const DEFAULT_SIZE: Size = { width: "auto", height: "auto" };
const DEFAULT_RESPONSIVE_POSITION: ResponsivePosition = { strategy: "flex" };
const COMPONENT_TYPE_VALUES = new Set<string>(Object.values(ComponentType));
const POSITIONING_STRATEGIES = new Set<string>([
  "absolute",
  "relative",
  "grid",
  "flex",
  "auto",
]);

interface TemplateWorkspace {
  templateIds: string[];
  currentTemplateId: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isCanvasSettings = (value: unknown): value is CanvasSettings => {
  if (!isRecord(value)) return false;
  return (
    isRecord(value.background) &&
    isRecord(value.grid) &&
    isRecord(value.layout) &&
    isRecord(value.theme) &&
    isRecord(value.responsive)
  );
};

const isComponentType = (value: unknown): value is ComponentType =>
  typeof value === "string" && COMPONENT_TYPE_VALUES.has(value);

const isResponsivePosition = (value: unknown): value is ResponsivePosition =>
  isRecord(value) &&
  typeof value.strategy === "string" &&
  POSITIONING_STRATEGIES.has(value.strategy);

const toPosition = (value: unknown): Position => {
  if (
    isRecord(value) &&
    typeof value.x === "number" &&
    typeof value.y === "number" &&
    typeof value.z === "number"
  ) {
    return { x: value.x, y: value.y, z: value.z };
  }
  return DEFAULT_POSITION;
};

const toSize = (value: unknown): Size => {
  if (
    isRecord(value) &&
    (typeof value.width === "number" || typeof value.width === "string") &&
    (typeof value.height === "number" || typeof value.height === "string")
  ) {
    return { width: value.width, height: value.height };
  }
  return DEFAULT_SIZE;
};

const toResponsivePosition = (value: unknown): ResponsivePosition => {
  return isResponsivePosition(value) ? value : DEFAULT_RESPONSIVE_POSITION;
};

const normalizeTemplateComponent = (value: unknown, index: number): Component | null => {
  if (!isRecord(value) || typeof value.id !== "string") return null;

  if (!isComponentType(value.type)) return null;

  const now = new Date();
  const children = Array.isArray(value.children)
    ? value.children.filter((child): child is string => typeof child === "string")
    : [];

  return {
    id: value.id,
    projectId: typeof value.projectId === "string" ? value.projectId : "template-workspace",
    pageId: typeof value.pageId === "string" ? value.pageId : "template-page",
    name: typeof value.name === "string" ? value.name : value.id,
    type: value.type,
    properties: isRecord(value.properties) ? value.properties : {},
    position: toPosition(value.position),
    size: toSize(value.size),
    style: isRecord(value.style) ? value.style : {},
    responsivePosition: toResponsivePosition(value.responsivePosition),
    parentId: typeof value.parentId === "string" ? value.parentId : undefined,
    children,
    order: typeof value.order === "number" ? value.order : index,
    locked: typeof value.locked === "boolean" ? value.locked : false,
    hidden: typeof value.hidden === "boolean" ? value.hidden : false,
    version: typeof value.version === "number" ? value.version : 1,
    createdBy: typeof value.createdBy === "string" ? value.createdBy : "template-user",
    lastModifiedBy:
      typeof value.lastModifiedBy === "string" ? value.lastModifiedBy : "template-user",
    lastModifiedAt: now,
    isDeleted: typeof value.isDeleted === "boolean" ? value.isDeleted : false,
    createdAt: now,
    updatedAt: now,
    isSubComponent:
      typeof value.isSubComponent === "boolean" ? value.isSubComponent : undefined,
    subComponentType:
      typeof value.subComponentType === "string" ? value.subComponentType : undefined,
    metadata: isRecord(value.metadata) ? value.metadata : undefined,
  };
};

const getTemplateComponents = (template: TemplateData): Component[] => {
  const firstPage = template.pages[0];
  if (!isRecord(firstPage) || !Array.isArray(firstPage.components)) {
    return [];
  }
  return firstPage.components
    .map((component, index) => normalizeTemplateComponent(component, index))
    .filter((component): component is Component => component !== null);
};

export const getTemplateCanvasSettingsForViewer = (template: TemplateData): CanvasSettings => {
  const firstPage = template.pages[0];
  if (isRecord(firstPage) && isCanvasSettings(firstPage.canvasSettings)) {
    return firstPage.canvasSettings;
  }
  if (isCanvasSettings(template.canvasSettings)) {
    return template.canvasSettings;
  }
  if (isCanvasSettings(template.settings)) {
    return template.settings;
  }
  return TEMPLATE_VIEWER_DEFAULT_CANVAS_SETTINGS;
};

// Helper to enhance components with children for containers (flex-container, container, card)
const enhanceComponentsWithChildren = (components: Component[]): Component[] => {
  const componentMap = new Map(components.map(c => [c.id, c]));
  const childComponentIds = new Set<string>();

  // First pass: collect all child component IDs
  components.forEach(component => {
    if (isContainerType(component.type) && component.children?.length > 0) {
      component.children.forEach(childId => childComponentIds.add(childId));
    }
  });

  // Recursive function to enhance a single component
  const enhanceComponent = (component: Component): Component => {
    if (isContainerType(component.type) && component.children?.length > 0) {
      const childComponents = component.children
        .map(childId => componentMap.get(childId))
        .filter((child): child is Component => child !== undefined)
        .map(child => enhanceComponent(child));

      return {
        ...component,
        _childComponents: childComponents
      };
    }
    return component;
  };

  return components
    .filter(component => !childComponentIds.has(component.id))
    .map(component => enhanceComponent(component));
};

// Component with runtime-added children from enhanceComponentsWithChildren
interface EnhancedComponent extends Component {
  _childComponents?: Component[];
}

// Build component map including nested children
const buildComponentMap = (components: Component[]): Map<string, Component> => {
  const map = new Map<string, Component>();
  const addComponent = (comp: Component) => {
    map.set(comp.id, comp);
    const enhanced = comp as EnhancedComponent;
    if (enhanced._childComponents) {
      enhanced._childComponents.forEach(child => addComponent(child));
    }
  };
  components.forEach(comp => addComponent(comp));
  return map;
};

export const useTemplateWorkspace = () => {
  const [workspace, setWorkspace] = useState<TemplateWorkspace>({
    templateIds: [],
    currentTemplateId: null,
  });

  const switchToPage = useViewerStore((state) => state.switchToPage);
  const clearProject = useViewerStore((state) => state.clearProject);
  const pages = useViewerStore((state) => state.pages);
  const currentPageId = useViewerStore((state) => state.currentPageId);

  // Load workspace templates into viewer store
  const loadWorkspaceIntoStore = useCallback((workspaceToLoad: TemplateWorkspace) => {
    const templates = workspaceToLoad.templateIds
      .map(id => {
        const data = getTemplateById(id);
        return data ? { id, data } : null;
      })
      .filter((t): t is { id: string; data: TemplateData } => t !== null);

    if (templates.length === 0) return;

    // Build pages from templates
    const now = new Date();
    const newPages: Page[] = templates.map((template, index) => ({
      id: `template-${template.id}`,
      projectId: "template-workspace",
      name: template.data.name || template.id,
      description: template.data.description,
      order: index,
      isDefault: index === 0,
      template: PageTemplate.CUSTOM,
      navigation: {
        showInNavigation: true,
        order: index,
        allowNavigation: true,
        requiresAuth: false,
      },
      permissions: {
        canView: [ProjectRole.VIEWER, ProjectRole.EDITOR, ProjectRole.ADMIN, ProjectRole.OWNER],
        canEdit: [ProjectRole.EDITOR, ProjectRole.ADMIN, ProjectRole.OWNER],
        canDelete: [ProjectRole.ADMIN, ProjectRole.OWNER],
        canShare: [ProjectRole.ADMIN, ProjectRole.OWNER],
        inheritFromProject: true,
        customPermissions: [],
      },
      canvasSettings: getTemplateCanvasSettingsForViewer(template.data),
      metadata: {
        viewCount: 0,
        editCount: 0,
        componentCount: getTemplateComponents(template.data).length,
        tags: [],
        customFields: {},
      },
      createdBy: "template-user",
      lastModifiedBy: "template-user",
      lastModifiedAt: now,
      createdAt: now,
      updatedAt: now,
    }));

    // Build page components map
    const pageComponents = new Map<string, Component[]>();
    templates.forEach(template => {
      const pageId = `template-${template.id}`;
      const components = getTemplateComponents(template.data);
      pageComponents.set(pageId, components);
    });

    // Find current page
    const currentPageId = workspaceToLoad.currentTemplateId
      ? `template-${workspaceToLoad.currentTemplateId}`
      : newPages[0].id;

    const currentPage = newPages.find(p => p.id === currentPageId) || newPages[0];
    const currentComponents = pageComponents.get(currentPage.id) || [];
    const enhancedComponents = enhanceComponentsWithChildren(currentComponents);

    // Load into store
    useViewerStore.setState({
      project: {
        id: "template-workspace",
        name: "Template Workspace",
        description: "Multi-template workspace",
        ownerId: "template-user",
        members: [],
        visibility: ProjectVisibility.PUBLIC,
        status: ProjectStatus.ACTIVE,
        settings: {
          autoSave: false,
          autoSaveInterval: 30000,
          maxVersions: 10,
          allowComments: false,
          allowRealTimeEditing: false,
          requireApproval: false,
          allowPageCreation: false,
          maxPages: 20,
          defaultPageTemplate: PageTemplate.CUSTOM,
          enableCrossPageLinks: false,
          sharedComponentLibrary: false,
        },
        tags: [],
        currentVersion: 1,
        versions: [],
        navigation: {
          navigationStyle: NavigationStyle.TABS,
          showPageTabs: true,
          showBreadcrumbs: false,
          enableSearch: false,
          compactMode: false,
          sidebarPosition: "left",
          tabsPosition: "top",
          customNavigationOrder: [],
          navigationGroups: [],
        },
        isMultiPage: true,
        pages: newPages,
        lastModifiedBy: "template-user",
        lastModifiedAt: new Date(),
        isDeleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      components: enhancedComponents,
      componentMap: buildComponentMap(enhancedComponents),
      pages: newPages,
      pageMap: new Map(newPages.map(p => [p.id, p])),
      pageComponents: pageComponents,
      currentPageId: currentPage.id,
      currentPage: currentPage,
      canvasSettings: currentPage.canvasSettings,
      viewport: Viewport.DESKTOP,
      isLoading: false,
      error: null,
      allowInteraction: true,
    });
  }, []);

  // Load workspace from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(WORKSPACE_STORAGE_KEY);
      if (saved) {
        const parseResult = TemplateWorkspaceSchema.safeParse(JSON.parse(saved));
        if (!parseResult.success) {
          console.warn('[useTemplateWorkspace] Workspace data schema mismatch, ignoring', parseResult.error.flatten());
          clearProject();
          return;
        }
        const savedWorkspace = parseResult.data;

        // Validate that templates still exist
        const validTemplateIds = savedWorkspace.templateIds.filter(id => {
          return getTemplateById(id) !== null;
        });

        if (validTemplateIds.length > 0) {
          const workspaceToLoad: TemplateWorkspace = {
            templateIds: validTemplateIds,
            currentTemplateId: validTemplateIds.includes(savedWorkspace.currentTemplateId || '')
              ? savedWorkspace.currentTemplateId ?? null
              : validTemplateIds[0],
          };

          setWorkspace(workspaceToLoad);
          loadWorkspaceIntoStore(workspaceToLoad);
          return;
        }
      }

      clearProject();
    } catch (err) {
      console.error("Failed to load template workspace:", err);
      clearProject();
    }
  }, [clearProject, loadWorkspaceIntoStore]);

  // Save workspace to localStorage whenever it changes
  useEffect(() => {
    if (workspace.templateIds.length > 0) {
      try {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(workspace));
      } catch (err) {
        console.error("Failed to save template workspace:", err);
      }
    }
  }, [workspace]);

  // Add template to workspace
  const addTemplate = useCallback((templateId: string) => {
    if (workspace.templateIds.includes(templateId)) {
      // Template already in workspace, just switch to it
      const newWorkspace = {
        ...workspace,
        currentTemplateId: templateId,
      };
      setWorkspace(newWorkspace);
      switchToPage(`template-${templateId}`);
      return;
    }

    const newWorkspace = {
      templateIds: [...workspace.templateIds, templateId],
      currentTemplateId: templateId,
    };

    setWorkspace(newWorkspace);
    loadWorkspaceIntoStore(newWorkspace);
    switchToPage(`template-${templateId}`);
  }, [workspace, switchToPage, loadWorkspaceIntoStore]);

  // Remove template from workspace
  const removeTemplate = useCallback((templateId: string) => {
    const newTemplateIds = workspace.templateIds.filter(id => id !== templateId);

    if (newTemplateIds.length === 0) {
      // Don't allow removing the last template
      return;
    }

    const newCurrentId = workspace.currentTemplateId === templateId
      ? newTemplateIds[0]
      : workspace.currentTemplateId;

    const newWorkspace = {
      templateIds: newTemplateIds,
      currentTemplateId: newCurrentId,
    };

    setWorkspace(newWorkspace);
    loadWorkspaceIntoStore(newWorkspace);
  }, [workspace, loadWorkspaceIntoStore]);

  // Switch to a template page
  const switchTemplate = useCallback((templateId: string) => {
    setWorkspace(prev => ({
      ...prev,
      currentTemplateId: templateId,
    }));
    switchToPage(`template-${templateId}`);
  }, [switchToPage]);

  // Get all available templates (memoized to prevent re-computation on every render)
  const allTemplates = useMemo(() => getAllTemplates(), []);
  const availableTemplates = useMemo(() => {
    const loadedSet = new Set(workspace.templateIds);
    return allTemplates.filter(t => !loadedSet.has(t.id));
  }, [allTemplates, workspace.templateIds]);

  // Get current template ID (without the "template-" prefix)
  const currentTemplateId = currentPageId ? currentPageId.replace('template-', '') : null;

  return {
    workspace,
    pages,
    currentPageId,
    currentTemplateId,
    addTemplate,
    removeTemplate,
    switchTemplate,
    allTemplates,
    availableTemplates,
  };
};
